import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Icon,
  IconButton,
  IconButtonProps,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SxProps,
} from '@mui/material';
import {
  Apps as AppsIcon,
  Login as LoginIcon,
  AppRegistration as AppRegistrationIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';

import { getAuthState } from '../../store/AuthSlice';
import { getAccessLimitState } from '../../store/AccessLimitSlice';
import { USER_PERMISSION, USER_ROLE_NAMES } from '../../store/UserSlice';
import { useAppSelector } from '../../hooks';
import { checkUserPermission } from '../../helper';
import {
  userNavigationRoutes,
  ADMIN_NAVIGATION_ROUTES,
  INavigationRoutes,
} from '../../navigation';

interface NavigationOptionsIconButtonProps {
  iconButtonProps?: IconButtonProps;
  iconStyles?: SxProps;
}

const NavigationOptionsIconButton: React.FC<NavigationOptionsIconButtonProps> = ({
  iconButtonProps = {},
  iconStyles = {},
}) => {
  const navigate = useNavigate();

  // Store
  const authState = useAppSelector(getAuthState);
  const accessLimitState = useAppSelector(getAccessLimitState);

  // State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Permissions
  const adminStudentPermission = checkUserPermission(authState.user, {
    adminStudent: ['view'],
  });

  const quizHistoryPermission = checkUserPermission(authState.user, {
    quizHistory: ['view'],
  });

  const coursePermission = checkUserPermission(authState.user, {
    course: USER_PERMISSION.COURSE,
  });

  const authUser = authState.user;
  const open = Boolean(anchorEl);
  const isSignInSignUpVisible =
    !authState.user ||
    (authState.user &&
      authState.user.role &&
      authState.user.role.name === USER_ROLE_NAMES.GUEST);

  const classBookingUrl = `${process.env.REACT_APP_TUTOPIYA_PLATFORM_BASE_URL}/#/dashboard/consultee/consultant`;
  const isTrialRecordingsInUrl = window.location.href.includes(
    'trial-recording'
  );

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const userNavigationFilter = (userNavigationRoute: INavigationRoutes) => {
    if (!authUser && userNavigationRoute.key !== 'home') {
      return false;
    }

    if (userNavigationRoute.key === 'students' && !adminStudentPermission) {
      return false;
    }

    if (userNavigationRoute.key === 'quiz-history' && !quizHistoryPermission) {
      return false;
    }

    if (
      userNavigationRoute.key === 'subscriptions' &&
      !accessLimitState.lpAccess
    ) {
      return false;
    }

    if (userNavigationRoute.key === 'courses' && !coursePermission) {
      return false;
    }

    return true;
  };

  const filterTutorNavigations = (navigationRoute: INavigationRoutes) => {
    if (authUser && authUser.role && authUser.role.name === 'Tutor') {
      return (
        navigationRoute.key === 'dashboard' ||
        navigationRoute.key === 'students'
      );
    }
    return true;
  };

  const handleNavigationChange = (userNavigationRoute: INavigationRoutes) => {
    setTimeout(() => {
      navigate(userNavigationRoute.pathname);
    }, 250);

    closeMenu();
  };

  let menuOptions: INavigationRoutes[] = [...userNavigationRoutes].filter(
    userNavigationFilter
  );

  if (authUser && authUser.role && authUser.role.type === 'admin') {
    menuOptions = [...ADMIN_NAVIGATION_ROUTES].filter(filterTutorNavigations);
  }

  return (
    <>
      <IconButton
        id="nav-bar-menu-button"
        aria-controls={open ? 'nav-bar-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        size="large"
        edge="start"
        color="primary"
        aria-label="menu"
        sx={{ mr: 2, ...iconStyles }}
        onClick={openMenu}
        {...iconButtonProps}
      >
        <AppsIcon />
      </IconButton>
      <Menu
        id="nav-bar-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        PaperProps={{ style: { borderRadius: '12px', minWidth: '300px' } }}
        MenuListProps={{
          'aria-labelledby': 'nav-bar-menu-button',
          style: { padding: '15px' },
        }}
      >
        {menuOptions.map((menuOption, index) => {
          // let isActive = false;

          // if (menuOption.matches && menuOption.matches.length > 0) {
          //   menuOption.matches.forEach((match) => {
          //     if (matchPath(match, location.pathname)) {
          //       isActive = true;
          //     }
          //   });
          // }

          return (
            <MenuItem
              key={index}
              // selected={isActive}
              onClick={() => handleNavigationChange(menuOption)}
              style={{ padding: '15px', borderRadius: '7px' }}
            >
              <ListItemIcon>
                <Icon
                  color="inherit"
                  // color={isActive ? 'primary' : 'inherit'}
                  component={menuOption.icon}
                />
              </ListItemIcon>
              <ListItemText
                primary={menuOption.name}
                primaryTypographyProps={{
                  color: 'secondary',
                  // color: isActive ? 'primary' : 'secondary',
                }}
              />
            </MenuItem>
          );
        })}
        {isSignInSignUpVisible ? (
          <div>
            <MenuItem
              onClick={() => navigate(`${process.env.PUBLIC_URL}/login`)}
              style={{ padding: '15px', borderRadius: '7px' }}
            >
              <ListItemIcon>
                <Icon color="inherit" component={LoginIcon} />
              </ListItemIcon>
              <ListItemText
                primary="Sign In"
                primaryTypographyProps={{
                  color: 'secondary',
                }}
              />
            </MenuItem>
            {!isTrialRecordingsInUrl ? (
              <MenuItem
                onClick={() => navigate(`${process.env.PUBLIC_URL}/sign-up`)}
                style={{ padding: '15px', borderRadius: '7px' }}
              >
                <ListItemIcon>
                  <Icon color="inherit" component={AppRegistrationIcon} />
                </ListItemIcon>
                <ListItemText
                  primary="Sign Up"
                  primaryTypographyProps={{
                    color: 'secondary',
                  }}
                />
              </MenuItem>
            ) : null}
            {isTrialRecordingsInUrl ? (
              <MenuItem
                component="a"
                href={classBookingUrl}
                target="_blank"
                onClick={closeMenu}
                style={{ padding: '15px', borderRadius: '7px' }}
              >
                <ListItemIcon>
                  <Icon color="inherit" component={CalendarTodayIcon} />
                </ListItemIcon>
                <ListItemText
                  primary="Book Classes With Tutopiya"
                  primaryTypographyProps={{
                    color: 'secondary',
                  }}
                />
              </MenuItem>
            ) : null}
          </div>
        ) : null}
      </Menu>
    </>
  );
};

export default NavigationOptionsIconButton;
